import { useEffect, useRef, useState } from "react";
import { MoreVertical, Copy, Download, Loader2 } from "lucide-react";

/**
 * Per-workflow overflow menu (Clone / Export).
 *
 * Export is only offered for workflows this org actually owns — provider-bundled
 * and global workflows are platform assets and the server refuses to export them
 * from a tenant, so offering the action would just produce a 403.
 */
export default function WorkflowCardMenu({ workflow, onClone, onExport, busy }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const ownedByOrg =
    workflow.visibility === "org" || workflow.visibility === "personal";

  const items = [
    onClone && {
      key: "clone",
      label: "Clone",
      hint: "Duplicate into this organization",
      icon: Copy,
      onClick: () => onClone(workflow)
    },
    onExport &&
      ownedByOrg && {
        key: "export",
        label: "Export",
        hint: "Download as a portable .json",
        icon: Download,
        onClick: () => onExport(workflow)
      }
  ].filter(Boolean);

  if (!items.length) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        className="flex items-center justify-center w-9 h-9 text-gray-500 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-gray-700 transition-colors disabled:opacity-50"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`More actions for ${workflow.name}`}
        title="More actions"
      >
        {busy ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <MoreVertical className="w-4 h-4" />
        )}
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1 z-20 w-60 py-1 bg-white border border-gray-200 rounded-lg shadow-lg"
        >
          {items.map((item) => (
            <button
              key={item.key}
              role="menuitem"
              onClick={() => {
                setOpen(false);
                item.onClick();
              }}
              className="w-full flex items-start gap-2.5 px-3 py-2 text-left hover:bg-gray-50"
            >
              <item.icon className="w-4 h-4 mt-0.5 text-gray-500 shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-800">
                  {item.label}
                </span>
                <span className="block text-xs text-gray-500">{item.hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
